import { useTranslation } from 'react-i18next'
import { Icon } from '../Icon/Icon'
import { cx } from '../utils/cx'
import type { DateRange, RangeCalendarProps } from './types'
import './RangeCalendar.css'

const pad = (n: number) => String(n).padStart(2, '0')
export const isoDate = (y: number, m: number, d: number) => `${y}-${pad(m)}-${pad(d)}`

export function shiftMonth(month: string, delta: number): string {
  const [y, m] = month.split('-').map(Number) as [number, number]
  const date = new Date(y, m - 1 + delta, 1)
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}`
}

function capitalize(s: string) {
  return s.charAt(0).toUpperCase() + s.slice(1)
}

/** Calendario de rango (bundle B5): celdas 44 px, flechas 44 px, rango en primary-soft con extremos en primary, ocupado tachado, actual subrayado, leyenda textual. */
export function RangeCalendar({ month, onMonthChange, value, onChange, current, busyDates = [], minDate, locale, className }: RangeCalendarProps) {
  const { t, i18n } = useTranslation()
  const lang = locale ?? i18n.language
  const [year, mon] = month.split('-').map(Number) as [number, number]
  const first = new Date(year, mon - 1, 1)
  const daysInMonth = new Date(year, mon, 0).getDate()
  const leading = (first.getDay() + 6) % 7 // lunes = 0
  const busy = new Set(busyDates)
  const monthLabel = `${capitalize(new Intl.DateTimeFormat(lang, { month: 'long' }).format(first))} ${year}`
  const weekdays = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(2024, 0, 1 + i) // 1 ene 2024 fue lunes
    return capitalize(new Intl.DateTimeFormat(lang, { weekday: 'narrow' }).format(d))
  })
  const longDate = (iso: string) => new Intl.DateTimeFormat(lang, { weekday: 'long', day: 'numeric', month: 'long' }).format(new Date(`${iso}T12:00:00`))

  const inRange = (iso: string, r: DateRange | undefined) => Boolean(r?.from && r.to && iso >= r.from && iso <= r.to)
  const isEdge = (iso: string, r: DateRange) => iso === r.from || iso === r.to

  const pick = (iso: string) => {
    if (!value.from || (value.from && value.to)) {
      onChange({ from: iso, to: null })
      return
    }
    if (iso <= value.from) {
      onChange({ from: iso, to: null })
      return
    }
    // Un rango no puede saltar por encima de un día ocupado.
    for (const b of busy) if (b > value.from && b < iso) return onChange({ from: iso, to: null })
    onChange({ from: value.from, to: iso })
  }

  return (
    <div className={cx('hp-cal', className)}>
      <div className="hp-cal__head">
        <span className="hp-cal__month" aria-live="polite">
          {monthLabel}
        </span>
        <div className="hp-cal__arrows">
          <button type="button" aria-label={t('v2.ds.calendar.mesAnterior')} onClick={() => onMonthChange(shiftMonth(month, -1))} className="hp-cal__arrow">
            <Icon name="chevron-left" size={22} />
          </button>
          <button type="button" aria-label={t('v2.ds.calendar.mesSiguiente')} onClick={() => onMonthChange(shiftMonth(month, 1))} className="hp-cal__arrow">
            <Icon name="chevron-down" size={22} className="hp-cal__arrow-next" />
          </button>
        </div>
      </div>
      <div className="hp-cal__weekdays" aria-hidden="true">
        {weekdays.map((w, i) => (
          <span key={i}>{w}</span>
        ))}
      </div>
      <div className="hp-cal__grid tnum" role="group" aria-label={monthLabel}>
        {Array.from({ length: leading }, (_, i) => (
          <span key={`lead-${i}`} className="hp-cal__gap" aria-hidden="true" />
        ))}
        {Array.from({ length: daysInMonth }, (_, i) => {
          const day = i + 1
          const iso = isoDate(year, mon, day)
          const isBusy = busy.has(iso)
          const disabled = isBusy || Boolean(minDate && iso < minDate)
          const selected = inRange(iso, value) || iso === value.from
          const edge = isEdge(iso, value)
          const isCurrent = inRange(iso, current) || iso === current?.from || iso === current?.to
          return (
            <button
              key={iso}
              type="button"
              aria-pressed={selected}
              aria-disabled={disabled || undefined}
              aria-label={`${longDate(iso)}${isBusy ? ` · ${t('v2.ds.calendar.ocupado')}` : ''}`}
              disabled={disabled}
              onClick={() => pick(iso)}
              className={cx(
                'hp-cal__day',
                selected && 'hp-cal__day--selected',
                edge && 'hp-cal__day--edge',
                iso === value.from && 'hp-cal__day--from',
                iso === value.to && 'hp-cal__day--to',
                isCurrent && 'hp-cal__day--current',
                isBusy && 'hp-cal__day--busy',
              )}
            >
              {day}
            </button>
          )
        })}
      </div>
      <div className="hp-cal__legend">
        <span>
          <span className="hp-cal__dot hp-cal__dot--new" aria-hidden="true" />
          {t('v2.ds.calendar.nuevasFechas')}
        </span>
        <span>
          <span className="hp-cal__dot hp-cal__dot--current" aria-hidden="true" />
          {t('v2.ds.calendar.fechasActuales')}
        </span>
        <span>
          <span className="hp-cal__busy-sample" aria-hidden="true">
            12
          </span>
          {t('v2.ds.calendar.ocupado')}
        </span>
      </div>
    </div>
  )
}
